export type ColorValue = string;

export type DataPoint = {
  x: string;
  y: number;
};

export type Trace = {
  label: string;
  color?: ColorValue;
  data: DataPoint[];
};

export type ReferenceRange = {
  label: string;
  min: number;
  max: number;
  color?: ColorValue;
};

export type BarSeries = {
  label: string;
  color?: ColorValue;
  values: number[];
};

export type ColumnDef = {
  key: string;
  label: string;
};

export const TABLE_CELL_STATUSES = [
  'positive',
  'warning',
  'negative',
  'neutral',
] as const;

export type TableCellStatus = (typeof TABLE_CELL_STATUSES)[number];

export type TableCell = {
  value: string;
  status?: TableCellStatus;
};

export type ComboSeries = {
  label: string;
  color?: ColorValue;
  render_as: 'bar' | 'line';
  axis: 'left' | 'right';
  values: number[];
};

export type LineChart = {
  type: 'line';
  minimum_version: string;
  title: string;
  x_label?: string;
  y_label?: string;
  traces: Trace[];
  reference_ranges: ReferenceRange[];
};

export type BarChart = {
  type: 'bar';
  minimum_version: string;
  title: string;
  categories: string[];
  series: BarSeries[];
  y_label?: string;
  orientation: 'vertical' | 'horizontal';
};

export type StackedBarChart = {
  type: 'stacked_bar';
  minimum_version: string;
  title: string;
  categories: string[];
  series: BarSeries[];
  y_label?: string;
};

export type ComboChart = {
  type: 'combo';
  minimum_version: string;
  title: string;
  categories: string[];
  x_label?: string;
  y_label_left?: string;
  y_label_right?: string;
  series: ComboSeries[];
};

export type ProgressChart = {
  type: 'progress';
  minimum_version: string;
  label: string;
  current: number;
  target: number;
  unit: string;
  color?: ColorValue;
};

export type StatCard = {
  type: 'stat';
  minimum_version: string;
  label: string;
  value: number;
  unit: string;
  delta?: number;
  delta_direction?: 'up' | 'down' | 'neutral';
};

export type TableChart = {
  type: 'table';
  minimum_version: string;
  title: string;
  columns: ColumnDef[];
  rows: Record<string, TableCell>[];
};

export type ChartSpec =
  | LineChart
  | BarChart
  | StackedBarChart
  | ComboChart
  | ProgressChart
  | StatCard
  | TableChart;

const DEFAULT_VERSION = '1.0.0';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const asString = (value: unknown, field: string): string => {
  if (typeof value !== 'string') {
    throw new Error(`expected ${field} to be a string`);
  }
  return value;
};

const asOptionalString = (
  value: unknown,
  field: string,
): string | undefined => {
  if (value === undefined || value === null) {
    return undefined;
  }
  return asString(value, field);
};

const asNumber = (value: unknown, field: string): number => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new Error(`expected ${field} to be a finite number`);
  }
  return value;
};

const asOptionalNumber = (
  value: unknown,
  field: string,
): number | undefined => {
  if (value === undefined || value === null) {
    return undefined;
  }
  return asNumber(value, field);
};

const asArray = (value: unknown, field: string): unknown[] => {
  if (!Array.isArray(value)) {
    throw new Error(`expected ${field} to be an array`);
  }
  return value;
};

/**
 * Charts derive their axis domains from the data, so an empty series or
 * category list would produce an infinite domain rather than a visible chart.
 */
const asNonEmptyArray = (value: unknown, field: string): unknown[] => {
  const array = asArray(value, field);
  if (array.length === 0) {
    throw new Error(`expected ${field} to be a non-empty array`);
  }
  return array;
};

/**
 * Category charts index values positionally, so a short series would render a
 * missing measurement as a real zero.
 */
const assertValuesMatchCategories = (
  series: BarSeries[] | ComboSeries[],
  categoryCount: number,
): void => {
  series.forEach((entry, i) => {
    if (entry.values.length !== categoryCount) {
      throw new Error(
        `expected series[${i}].values to have one value per category (${categoryCount})`,
      );
    }
  });
};

/**
 * Line traces share a single x axis labelled from the first trace, so
 * mismatched x values would plot points against the wrong dates.
 */
const assertTracesShareXValues = (traces: Trace[]): void => {
  const [first, ...rest] = traces;
  const expected = first.data.map(point => point.x);
  rest.forEach((trace, i) => {
    const actual = trace.data.map(point => point.x);
    if (
      actual.length !== expected.length ||
      actual.some((x, j) => x !== expected[j])
    ) {
      throw new Error(
        `expected traces[${i + 1}].data to use the same x values as traces[0]`,
      );
    }
  });
};

const parseDataPoint = (value: unknown, field: string): DataPoint => {
  if (!isRecord(value)) {
    throw new Error(`expected ${field} to be an object`);
  }
  return {
    x: asString(value.x, `${field}.x`),
    y: asNumber(value.y, `${field}.y`),
  };
};

const parseTrace = (value: unknown, field: string): Trace => {
  if (!isRecord(value)) {
    throw new Error(`expected ${field} to be an object`);
  }
  return {
    label: asString(value.label, `${field}.label`),
    color: asOptionalString(value.color, `${field}.color`),
    data: asNonEmptyArray(value.data, `${field}.data`).map((point, i) =>
      parseDataPoint(point, `${field}.data[${i}]`),
    ),
  };
};

const parseReferenceRange = (value: unknown, field: string): ReferenceRange => {
  if (!isRecord(value)) {
    throw new Error(`expected ${field} to be an object`);
  }
  return {
    label: asString(value.label, `${field}.label`),
    min: asNumber(value.min, `${field}.min`),
    max: asNumber(value.max, `${field}.max`),
    color: asOptionalString(value.color, `${field}.color`),
  };
};

const parseBarSeries = (value: unknown, field: string): BarSeries => {
  if (!isRecord(value)) {
    throw new Error(`expected ${field} to be an object`);
  }
  return {
    label: asString(value.label, `${field}.label`),
    color: asOptionalString(value.color, `${field}.color`),
    values: asArray(value.values, `${field}.values`).map((v, i) =>
      asNumber(v, `${field}.values[${i}]`),
    ),
  };
};

const parseComboSeries = (value: unknown, field: string): ComboSeries => {
  if (!isRecord(value)) {
    throw new Error(`expected ${field} to be an object`);
  }
  const renderAs = value.render_as ?? 'bar';
  const axis = value.axis ?? 'left';
  if (renderAs !== 'bar' && renderAs !== 'line') {
    throw new Error(`expected ${field}.render_as to be bar or line`);
  }
  if (axis !== 'left' && axis !== 'right') {
    throw new Error(`expected ${field}.axis to be left or right`);
  }
  return {
    label: asString(value.label, `${field}.label`),
    color: asOptionalString(value.color, `${field}.color`),
    render_as: renderAs,
    axis,
    values: asArray(value.values, `${field}.values`).map((v, i) =>
      asNumber(v, `${field}.values[${i}]`),
    ),
  };
};

const parseColumnDef = (value: unknown, field: string): ColumnDef => {
  if (!isRecord(value)) {
    throw new Error(`expected ${field} to be an object`);
  }
  return {
    key: asString(value.key, `${field}.key`),
    label: asString(value.label, `${field}.label`),
  };
};

const isTableCellStatus = (value: unknown): value is TableCellStatus =>
  typeof value === 'string' &&
  (TABLE_CELL_STATUSES as readonly string[]).includes(value);

const parseTableCell = (value: unknown, field: string): TableCell => {
  if (!isRecord(value)) {
    throw new Error(`expected ${field} to be an object`);
  }
  const status = value.status;
  if (status !== undefined && status !== null && !isTableCellStatus(status)) {
    throw new Error(
      `expected ${field}.status to be ${TABLE_CELL_STATUSES.join(', ')}`,
    );
  }
  const rawValue = value.value;
  const cellValue =
    typeof rawValue === 'number' && Number.isFinite(rawValue)
      ? String(rawValue)
      : asString(rawValue, `${field}.value`);
  return {
    value: cellValue,
    status: isTableCellStatus(status) ? status : undefined,
  };
};

const parseRow = (value: unknown, field: string): Record<string, TableCell> => {
  if (!isRecord(value)) {
    throw new Error(`expected ${field} to be an object`);
  }
  return Object.fromEntries(
    Object.entries(value).map(([key, cell]) => [
      key,
      parseTableCell(cell, `${field}.${key}`),
    ]),
  );
};

export const parseChartSpec = (raw: unknown): ChartSpec => {
  if (!isRecord(raw)) {
    throw new Error('chart spec must be an object');
  }
  const minimum_version =
    asOptionalString(raw.minimum_version, 'minimum_version') ?? DEFAULT_VERSION;

  switch (raw.type) {
    case 'line': {
      const traces = asNonEmptyArray(raw.traces, 'traces').map((trace, i) =>
        parseTrace(trace, `traces[${i}]`),
      );
      assertTracesShareXValues(traces);
      return {
        type: 'line',
        minimum_version,
        title: asString(raw.title, 'title'),
        x_label: asOptionalString(raw.x_label, 'x_label'),
        y_label: asOptionalString(raw.y_label, 'y_label'),
        traces,
        reference_ranges: asArray(
          raw.reference_ranges ?? [],
          'reference_ranges',
        ).map((range, i) =>
          parseReferenceRange(range, `reference_ranges[${i}]`),
        ),
      };
    }
    case 'bar': {
      const orientation = raw.orientation ?? 'vertical';
      if (orientation !== 'vertical' && orientation !== 'horizontal') {
        throw new Error('orientation must be vertical or horizontal');
      }
      const categories = asNonEmptyArray(raw.categories, 'categories').map(
        (c, i) => asString(c, `categories[${i}]`),
      );
      const series = asNonEmptyArray(raw.series, 'series').map((s, i) =>
        parseBarSeries(s, `series[${i}]`),
      );
      assertValuesMatchCategories(series, categories.length);
      return {
        type: 'bar',
        minimum_version,
        title: asString(raw.title, 'title'),
        categories,
        series,
        y_label: asOptionalString(raw.y_label, 'y_label'),
        orientation,
      };
    }
    case 'stacked_bar': {
      const categories = asNonEmptyArray(raw.categories, 'categories').map(
        (c, i) => asString(c, `categories[${i}]`),
      );
      const series = asNonEmptyArray(raw.series, 'series').map((s, i) =>
        parseBarSeries(s, `series[${i}]`),
      );
      assertValuesMatchCategories(series, categories.length);
      return {
        type: 'stacked_bar',
        minimum_version,
        title: asString(raw.title, 'title'),
        categories,
        series,
        y_label: asOptionalString(raw.y_label, 'y_label'),
      };
    }
    case 'combo': {
      const categories = asNonEmptyArray(raw.categories, 'categories').map(
        (c, i) => asString(c, `categories[${i}]`),
      );
      const series = asNonEmptyArray(raw.series, 'series').map((s, i) =>
        parseComboSeries(s, `series[${i}]`),
      );
      assertValuesMatchCategories(series, categories.length);
      return {
        type: 'combo',
        minimum_version,
        title: asString(raw.title, 'title'),
        categories,
        x_label: asOptionalString(raw.x_label, 'x_label'),
        y_label_left: asOptionalString(raw.y_label_left, 'y_label_left'),
        y_label_right: asOptionalString(raw.y_label_right, 'y_label_right'),
        series,
      };
    }
    case 'progress':
      return {
        type: 'progress',
        minimum_version,
        label: asString(raw.label, 'label'),
        current: asNumber(raw.current, 'current'),
        target: asNumber(raw.target, 'target'),
        unit: asString(raw.unit, 'unit'),
        color: asOptionalString(raw.color, 'color'),
      };
    case 'stat': {
      const deltaDirection = raw.delta_direction;
      if (
        deltaDirection !== undefined &&
        deltaDirection !== null &&
        deltaDirection !== 'up' &&
        deltaDirection !== 'down' &&
        deltaDirection !== 'neutral'
      ) {
        throw new Error('delta_direction must be up, down, or neutral');
      }
      return {
        type: 'stat',
        minimum_version,
        label: asString(raw.label, 'label'),
        value: asNumber(raw.value, 'value'),
        unit: asString(raw.unit, 'unit'),
        delta: asOptionalNumber(raw.delta, 'delta'),
        delta_direction: deltaDirection ?? undefined,
      };
    }
    case 'table':
      return {
        type: 'table',
        minimum_version,
        title: asString(raw.title, 'title'),
        columns: asNonEmptyArray(raw.columns, 'columns').map((c, i) =>
          parseColumnDef(c, `columns[${i}]`),
        ),
        rows: asArray(raw.rows, 'rows').map((row, i) =>
          parseRow(row, `rows[${i}]`),
        ),
      };
    default:
      throw new Error(`unsupported chart type: ${String(raw.type)}`);
  }
};
